import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { colors2, UI_BODY_SM_DF, DateRangePicker } from "oolib";
import { ButtonPrimary, ButtonSecondary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
  ConnectedBanner,
  ConnectedMeta,
  StaleBanner,
  ErrorBanner,
} from "./BaseIntegrationInterface";
import { useOAuthConnection } from "../../hooks/useOAuthConnection";
import { IntegrationDisconnectConfirmModal } from "@/_self_managed/SM_Integrations/IntegrationDisconnectConfirmModal";

// ============================================
// FATHOM ICON
// ============================================

export const FathomIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 1000 1000"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#007299"
      d="M0,668.7v205.78c0,53.97,34.24,102.88,85.8,119.08,87.48,27.49,167.88-36.99,167.88-120.22v-77.45L0,668.7Z"
    />
    <path
      fill="#00beff"
      d="M873.72,626.07c-19.05,0-38.38-4.3-56.58-13.38L72.78,241.43C11.15,210.69-17.51,136.6,11.18,74.05,41.2,8.59,119.26-18.53,183.23,13.38l744.25,371.21c62.45,31.15,91,109.08,59.79,171.43-22.22,44.38-67.02,70.05-113.55,70.05Z"
    />
    <path
      fill="#00beff"
      d="M500.09,813.66c-19.05,0-38.38-4.3-56.58-13.38l-370.72-184.9c-61.63-30.74-90.29-104.82-61.61-167.37,30.02-65.46,108.08-92.59,172.06-60.68l370.62,184.85c62.45,31.15,91,109.08,59.79,171.43-22.22,44.38-67.02,70.05-113.55,70.05Z"
    />
  </svg>
);

const DAY_MS = 24 * 60 * 60 * 1000;
const SOFT_WARN_DAYS = 90;
const HARD_CAP_DAYS = 365;
const isoDaysAgo = (n) => new Date(Date.now() - n * DAY_MS).toISOString().slice(0, 10);
const todayIso = () => new Date().toISOString().slice(0, 10);

const DateRangeRow = styled.div`
  margin-top: 16px;
`;

const RangeHint = styled(UI_BODY_SM_DF)`
  color: ${({ $error }) => ($error ? "#c0392b" : colors2.grey60)};
  margin-top: 8px;
`;

const TranscriptWarning = styled(UI_BODY_SM_DF)`
  color: #8a6d00;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 16px;
`;

export const FathomInterface = ({ onSubmit, isLoading }) => {
  const {
    connection,
    connectionLoading,
    connectError,
    handleConnect,
    isConnecting,
    handleDisconnect,
  } = useOAuthConnection({
    providerId: "fathom",
    providerName: "Fathom",
    preflightEnabled: false,
  });

  const [dateRange, setDateRange] = useState([isoDaysAgo(365), todayIso()]);
  const [startDate, endDate] = dateRange;

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await handleDisconnect();
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const rangeDays = startDate && endDate
    ? Math.round((new Date(endDate) - new Date(startDate)) / DAY_MS)
    : 0;
  const rangeTooLong = rangeDays > HARD_CAP_DAYS;
  const rangeWarn = !rangeTooLong && rangeDays > SOFT_WARN_DAYS;
  const rangeInverted = startDate && endDate && new Date(startDate) > new Date(endDate);

  const canFetch = connection?.connected
    && connection?.status !== "stale"
    && !rangeTooLong
    && !rangeInverted
    && !isLoading;

  const handleFetch = () => {
    if (!canFetch) return;
    // Send the user-local day boundaries as UTC instants. The user picked these
    // dates in their own timezone, so start date -> start of that local day
    // (00:00) and end date -> end of that local day (23:59:59.999); moment's
    // .toISOString() then serializes each to the correct UTC equivalent before
    // Fathom sees it (e.g. 2025-05-03 00:00 IST -> 2025-05-02T18:30:00.000Z).
    const createdAfter = startDate ? moment(startDate).startOf("day").toISOString() : null;
    const createdBefore = endDate ? moment(endDate).endOf("day").toISOString() : null;
    onSubmit({
      adapterOptions: {
        ...(createdAfter ? { createdAfter } : {}),
        ...(createdBefore ? { createdBefore } : {}),
      },
    });
  };

  if (connectionLoading) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Fathom</InterfaceHeader>
        <InterfaceDescription>Checking connection status…</InterfaceDescription>
      </InterfaceContainer>
    );
  }

  if (!connection?.connected) {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Connect to Fathom</InterfaceHeader>
        <InterfaceDescription>
          Import your meeting transcripts from Fathom AI. You'll be redirected
          to Fathom to authorize access, then brought back here to pick a date
          range and run the import.
        </InterfaceDescription>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
        <InterfaceActions>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Connect Fathom</ButtonPrimary>
        </InterfaceActions>
        <HelpText>
          Dots uses OAuth to access your Fathom workspace. We only request read
          access to meetings and transcripts — your team never sees the raw API
          key.
        </HelpText>
      </InterfaceContainer>
    );
  }

  if (connection.status === "stale") {
    return (
      <InterfaceContainer>
        <InterfaceHeader>Reconnect Fathom</InterfaceHeader>
        <StaleBanner>
          <ConnectedMeta>
            <strong>⚠ Connection needs to be renewed</strong>
            <span>
              {connection.providerAccountName
                ? `Your Fathom connection to ${connection.providerAccountName} expired or was revoked.`
                : "Your Fathom connection expired or was revoked."}
            </span>
          </ConnectedMeta>
          <ButtonPrimary onClick={handleConnect} isLoading={isConnecting} disabled={isConnecting}>Reconnect Fathom</ButtonPrimary>
        </StaleBanner>
        {connectError && <ErrorBanner>{connectError}</ErrorBanner>}
      </InterfaceContainer>
    );
  }

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Fathom</InterfaceHeader>
      <InterfaceDescription>
        Each meeting becomes one row. Transcripts are formatted as
        speaker-prefixed text.
      </InterfaceDescription>

      <ConnectedBanner>
        <ConnectedMeta>
          <strong>
            ✓ Connected{connection.providerAccountName ? ` as ${connection.providerAccountName}` : ""}
          </strong>
          {connection.adminEmail && (
            <span style={{ color: colors2.grey60 }}>{connection.adminEmail}</span>
          )}
        </ConnectedMeta>
        <ButtonSecondary onClick={() => setShowDisconnectConfirm(true)} disabled={isLoading}>
          Disconnect
        </ButtonSecondary>
      </ConnectedBanner>

      {showDisconnectConfirm && (
        <IntegrationDisconnectConfirmModal
          name="Fathom"
          onConfirm={confirmDisconnect}
          onClose={() => setShowDisconnectConfirm(false)}
          isLoading={isDisconnecting}
        />
      )}

      {connectError && <ErrorBanner>{connectError}</ErrorBanner>}

      <DateRangeRow>
        <DateRangePicker
          id="fathom-date-range"
          label="Date Range"
          value={dateRange}
          onChange={(k, v) => setDateRange(v)}
          previousDateOnly
          readOnly={isLoading}
        />
      </DateRangeRow>

      {rangeInverted && (
        <RangeHint $error>End date must be on or after start date.</RangeHint>
      )}
      {rangeTooLong && (
        <RangeHint $error>
          Please select a range of {HARD_CAP_DAYS} days or fewer. Run multiple imports for longer history.
        </RangeHint>
      )}
      {rangeWarn && (
        <RangeHint>
          Large date ranges ({rangeDays} days) may take several minutes to import.
        </RangeHint>
      )}
      <RangeHint>
        Heads up: large imports (more than ~25 meetings) may time out — try a
        shorter range first if you have lots of meetings.
      </RangeHint>

      <TranscriptWarning>
        Note: Fathom may occasionally miss portions of the meeting transcript due to
        limitations on their side. Please review the transcript to ensure it is
        complete and accurate before relying on it.
      </TranscriptWarning>

      <InterfaceActions>
        <ButtonPrimary onClick={handleFetch} disabled={!canFetch} isLoading={isLoading}>
          Fetch Meetings
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

export const fathomIntegrationMeta = {
  id: "fathom",
  name: "Fathom",
  description: "Import meeting transcripts from Fathom AI",
};

export default FathomInterface;
